import * as React from 'react';
import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { IAppRoute, IAppRouteGroup, isAppRouteForUser, NavSection, routes } from '@app/routes';
import { useEntraAppRoles } from '@app/hooks/useEntraRoles';
import teladocLogo from '@app/bgimages/teladoc-logo.png';
import { CloudBeesIcon, GitHubIcon, JFrogIcon, AzureIcon, GrafanaIcon, DocsIcon, NewRelicIcon } from '@app/components/BrandIcons';
import { ActiveAlertBanner } from './ActiveAlertBanner';
import { ENABLE_ALERTS } from '../config';
import './app-shell.css';
import './pages.css';

interface IAppLayout {
  children: React.ReactNode;
}

const NAV_SECTION_META: Record<NavSection, { title: string; isAi?: boolean }> = {
  generators: { title: '🛠️ GENERATORS' },
  ai: { title: '🤖 AI HUB' },
  build: { title: '🔨 BUILD' },
  deploy: { title: '🚢 DEPLOY' },
  secretmanager: { title: '🔐 SECRET MANAGER' },
  resources: { title: '🔗 RESOURCES' },
  analytics: { title: '📡 ANALYTICS' },
  monitoring: { title: '📈 MONITORING' },
  admin: { title: '⚙️ ADMIN' },
};

const SECTION_ORDER: NavSection[] = ['generators', 'build', 'deploy', 'ai', 'analytics', 'monitoring', 'resources', 'admin'];

const AppLayout: React.FunctionComponent<IAppLayout> = ({ children }) => {
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showUserInfo, setShowUserInfo] = useState(false);
  const [isDarkTheme, setIsDarkTheme] = useState(() => localStorage.getItem('devex-dark-theme') === 'true');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('devex-sidebar-collapsed') === 'true');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [openHeaderMenu, setOpenHeaderMenu] = useState<string | null>(null);

  const userMenuRef = useRef<HTMLDivElement>(null);
  const headerActionsRef = useRef<HTMLDivElement>(null);

  const { instance, accounts } = useMsal();
  const location = useLocation();
  const userRoles = useEntraAppRoles();
  useEffect(() => {
    const dark = localStorage.getItem('devex-dark-theme') === 'true';
    document.documentElement.classList.toggle('light-theme', !dark);
    document.body.classList.toggle('light-theme', !dark);
  }, []);

  // Auto-expand nav groups that contain the active route
  useEffect(() => {
    const newExpanded: Record<string, boolean> = {};
    routes.forEach((route) => {
      const group = route as IAppRouteGroup;
      if (group.routes) {
        const isActive = group.routes.some((r) => location.pathname.includes(r.path));
        if (isActive) {
          newExpanded[group.label] = true;
        }
      }
    });
    setExpandedGroups((prev) => ({ ...prev, ...newExpanded }));
  }, [location.pathname]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutPopup({ account: accounts[0] });
      window.location.reload();
    } catch (popupError) {
      console.warn('[Logout] Popup logout failed, falling back to redirect', popupError);
      instance.logoutRedirect({
        onRedirectNavigate: () => false,
      });
    }
  }, [instance, accounts]);

  const toggleTheme = useCallback(() => {
    setIsDarkTheme((prev) => {
      const next = !prev;
      localStorage.setItem('devex-dark-theme', String(next));
      document.documentElement.classList.toggle('light-theme', !next);
      document.body.classList.toggle('light-theme', !next);
      return next;
    });
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem('devex-sidebar-collapsed', String(next));
      return next;
    });
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserDropdown(false);
      }
      if (headerActionsRef.current && !headerActionsRef.current.contains(e.target as Node)) {
        setOpenHeaderMenu(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowUserDropdown(false);
        setOpenHeaderMenu(null);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const isRouteActive = useCallback(
    (route: IAppRoute) => {
      if (route.exact) return route.path === location.pathname;
      return location.pathname.includes(route.path);
    },
    [location.pathname]
  );

  // Group routes by navSection
  const sectionedRoutes = useMemo(() => {
    const sections: Record<string, Array<IAppRoute | IAppRouteGroup>> = {};
    routes.forEach((route) => {
      const isHidden = (route as IAppRouteGroup).showInMainNav === false;
      if (isHidden || route.visible === false || !route.label) return;
      if (!isAppRouteForUser(route as IAppRoute, userRoles)) return;
      const section = route.navSection;
      if (!section) return;
      if (!sections[section]) sections[section] = [];
      sections[section].push(route);
    });
    return sections;
  }, [userRoles]);


  const toggleNavGroup = useCallback((label: string) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  }, []);

  // Get page title from current route
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const userName = accounts[0]?.name || 'User';

  // --- Render helpers ---

  const AiAgentsIcon = () => (
    <svg width="20" height="20" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      <rect width="64" height="64" rx="14" fill="#1e3a5f"/>
      <line x1="32" y1="8" x2="32" y2="15" stroke="#60a5fa" strokeWidth="2.5" strokeLinecap="round"/>
      <circle cx="32" cy="6.5" r="2.5" fill="#60a5fa"/>
      <rect x="16" y="15" width="32" height="22" rx="6" fill="white" opacity="0.95"/>
      <rect x="21" y="22" width="8" height="6" rx="2" fill="#1e40af"/>
      <rect x="35" y="22" width="8" height="6" rx="2" fill="#1e40af"/>
      <rect x="22" y="23" width="3" height="2" rx="1" fill="#93c5fd" opacity="0.8"/>
      <rect x="36" y="23" width="3" height="2" rx="1" fill="#93c5fd" opacity="0.8"/>
      <rect x="23" y="31" width="18" height="3" rx="1.5" fill="#bfdbfe"/>
      <rect x="28" y="37" width="8" height="4" rx="2" fill="#cbd5e1"/>
      <rect x="14" y="41" width="36" height="18" rx="7" fill="white" opacity="0.92"/>
      <rect x="22" y="45" width="20" height="10" rx="3" fill="#dbeafe"/>
      <circle cx="27" cy="50" r="2" fill="#2563eb"/>
      <circle cx="32" cy="50" r="2" fill="#3b82f6"/>
      <circle cx="37" cy="50" r="2" fill="#2563eb"/>
      <line x1="27" y1="50" x2="37" y2="50" stroke="#93c5fd" strokeWidth="1" opacity="0.7"/>
      <rect x="6" y="42" width="8" height="14" rx="4" fill="white" opacity="0.85"/>
      <rect x="50" y="42" width="8" height="14" rx="4" fill="white" opacity="0.85"/>
    </svg>
  );

  const renderNavIcon = (icon?: string) => {
    if (icon === '__ai-agents__') return <AiAgentsIcon />;
    if (icon && (icon.startsWith('/') || icon.startsWith('http'))) {
      return <img src={icon} alt="" className="devex-nav-item-icon-img" />;
    }
    return <span className="devex-nav-item-icon">{icon || '📄'}</span>;
  };

  const renderNavItem = (route: IAppRoute, index: number) => (
    <NavLink
      key={`${route.label}-${index}`}
      to={route.path}
      exact={route.exact}
      className={`devex-nav-item${isRouteActive(route) ? ' active' : ''}`}
      activeClassName="active"
    >
      {renderNavIcon(route.navIcon)}
      <span className="devex-nav-label">{route.label}</span>
    </NavLink>
  );

  const renderNavGroup = (group: IAppRouteGroup, groupIndex: number) => {
    const isExpanded = expandedGroups[group.label] || false;
    return (
      <div key={`${group.label}-${groupIndex}`} className={`devex-nav-group${isExpanded ? ' expanded' : ''}`}>
        <button className="devex-nav-group-title" onClick={() => toggleNavGroup(group.label)}>
          {renderNavIcon(group.navIcon || '📁')}
          <span className="devex-nav-label">{group.label}</span>
          <span className="devex-nav-group-arrow">›</span>
        </button>
        <div className="devex-nav-group-items">
          {group.routes.map((route, idx) => route.label && renderNavItem(route, idx))}
        </div>
      </div>
    );
  };

  const renderSidebarContent = () => {
    return (
      <>
        {SECTION_ORDER.map((sectionKey) => {
          const items = sectionedRoutes[sectionKey];
          if (!items || items.length === 0) return null;
          const meta = NAV_SECTION_META[sectionKey];
          return (
            <div
              key={sectionKey}
              className={`devex-nav-section${meta.isAi ? ' devex-nav-section-ai' : ''}`}
            >
              <div className="devex-nav-section-title">
                {meta.title}
                {meta.isAi && <span className="devex-new-badge">NEW</span>}
              </div>
              {items.map((route, idx) => {
                if ((route as IAppRouteGroup).routes) {
                  return renderNavGroup(route as IAppRouteGroup, idx);
                }
                return renderNavItem(route as IAppRoute, idx);
              })}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <>
      <div className="devex-app">
        {ENABLE_ALERTS ? <ActiveAlertBanner /> : <></>}
        <div className="devex-app-body">
        {/* Sidebar */}
        <div className={`devex-sidebar-wrapper${sidebarCollapsed ? ' collapsed' : ''}`}>
          <nav className="devex-sidebar">
            <a href="/" className="devex-logo">
              <img src={teladocLogo} alt="Teladoc Health" className="devex-logo-icon" />
              <span className="devex-logo-text">DevEx Portal</span>
            </a>
            <div className="devex-sidebar-nav">
              {renderSidebarContent()}
            </div>
            <div
              ref={userMenuRef}
              className={`devex-sidebar-user${showUserDropdown ? ' open' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                setShowUserDropdown((prev) => !prev);
              }}
            >
              <div className="devex-user-avatar">{getInitials(userName)}</div>
              <span className="devex-nav-label">{userName}</span>
              <span className="devex-nav-label devex-user-menu-arrow">▲</span>
              {showUserDropdown && (
                <div className="devex-sidebar-user-dropdown">
                  <button
                    className="devex-user-dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowUserInfo(true);
                      setShowUserDropdown(false);
                    }}
                  >
                    👤 Profile
                  </button>
                  <button
                    className="devex-user-dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      logout();
                    }}
                  >
                    🔄 Refresh token
                  </button>
                </div>
              )}
            </div>
          </nav>
          <button className="devex-sidebar-toggle" onClick={toggleSidebar} aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              {sidebarCollapsed
                ? <path d="M6 3l5 5-5 5V3z"/>
                : <path d="M10 3L5 8l5 5V3z"/>
              }
            </svg>
          </button>
        </div>

        {/* Main Content */}
        <main className="devex-main">
          {/* Header */}
          <header className="devex-header">
            <div className="devex-header-actions" ref={headerActionsRef}>
              <div className="devex-header-dropdown">
                <button className="devex-header-link-btn" aria-label="GitHub" onClick={() => setOpenHeaderMenu(openHeaderMenu === 'github' ? null : 'github')}>
                  <GitHubIcon size={16} />
                  <span>GitHub</span>
                  <svg className="devex-chevron" width="10" height="10" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true"><path d="M2 3.5L5 7l3-3.5z"/></svg>
                </button>
                {openHeaderMenu === 'github' && (
                  <div className="devex-header-dropdown-menu">
                    <a href="https://github.com/Teladoc" target="_blank" rel="noopener noreferrer" onClick={() => setOpenHeaderMenu(null)}>
                      <GitHubIcon size={14} /> Teladoc GitHub
                    </a>
                    <a href="https://github.com/livongo" target="_blank" rel="noopener noreferrer" onClick={() => setOpenHeaderMenu(null)}>
                      <GitHubIcon size={14} /> Livongo GitHub
                    </a>
                    <a href="https://github.com/intouchHealth" target="_blank" rel="noopener noreferrer" onClick={() => setOpenHeaderMenu(null)}>
                      <GitHubIcon size={14} /> InTouch GitHub
                    </a>
                  </div>
                )}
              </div>

              <a href="https://ci.intouchhealth.io/cjoc/" target="_blank" rel="noopener noreferrer" className="devex-header-link-btn" aria-label="CloudBees">
                <CloudBeesIcon size={16} color="#1D8ECA" />
                <span>CloudBees</span>
              </a>

              <a href="https://artifactory.intouchhealth.io" target="_blank" rel="noopener noreferrer" className="devex-header-link-btn" aria-label="Artifactory">
                <JFrogIcon size={16} color="#40BE46" />
                <span>Artifactory</span>
              </a>

              <div className="devex-header-dropdown">
                <button className="devex-header-link-btn" aria-label="Azure Portal" aria-haspopup="true" aria-expanded={openHeaderMenu === 'azure'} aria-controls="devex-header-azure-menu" onClick={() => setOpenHeaderMenu(openHeaderMenu === 'azure' ? null : 'azure')}>
                  <AzureIcon size={16} color="#0078D4" />
                  <span>Azure Portal</span>
                  <svg className="devex-chevron" width="10" height="10" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true"><path d="M2 3.5L5 7l3-3.5z"/></svg>
                </button>
                {openHeaderMenu === 'azure' && (
                  <div className="devex-header-dropdown-menu" id="devex-header-azure-menu">
                    <a href="https://portal.azure.com/@teladochealth.com" target="_blank" rel="noopener noreferrer" onClick={() => setOpenHeaderMenu(null)}>
                      <AzureIcon size={14} color="#0078D4" /> Azure Portal (Corporate)
                    </a>
                    <a href="https://portal.azure.com/@tdhcommercial.onmicrosoft.com" target="_blank" rel="noopener noreferrer" onClick={() => setOpenHeaderMenu(null)}>
                      <AzureIcon size={14} color="#0078D4" /> Azure Portal (Commercial)
                    </a>
                  </div>
                )}
              </div>

              <div className="devex-header-dropdown">
                <button className="devex-header-link-btn" aria-label="Monitoring" onClick={() => setOpenHeaderMenu(openHeaderMenu === 'observability' ? null : 'observability')}>
                  <GrafanaIcon size={16} color="#F46800" />
                  <span>Monitoring</span>
                  <svg className="devex-chevron" width="10" height="10" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true"><path d="M2 3.5L5 7l3-3.5z"/></svg>
                </button>
                {openHeaderMenu === 'observability' && (
                  <div className="devex-header-dropdown-menu">
                    <a href="https://grafana.gw.eastus2.private.manage.tdoctest.com/" target="_blank" rel="noopener noreferrer" onClick={() => setOpenHeaderMenu(null)}>
                      <GrafanaIcon size={14} color="#F46800" /> Grafana - Preprod
                    </a>
                    <a href="https://grafana.gw.eastus2.private.mgmt.tdoctest.com/" target="_blank" rel="noopener noreferrer" onClick={() => setOpenHeaderMenu(null)}>
                      <GrafanaIcon size={14} color="#F46800" /> Grafana - Non-Prod
                    </a>
                    <a href="https://launcher.myapps.microsoft.com/api/signin/95df8dfa-0f32-4484-95f0-4841116f7642?tenantId=c162a585-4fef-44bd-9271-d96409d0a349" target="_blank" rel="noopener noreferrer" onClick={() => setOpenHeaderMenu(null)}>
                      <NewRelicIcon size={14} color="#00ac69" /> New Relic
                    </a>
                  </div>
                )}
              </div>

              <a href="https://engineering.docs.teladoc.dev/#/" target="_blank" rel="noopener noreferrer" className="devex-header-link-btn" aria-label="Engineering Docs">
                <DocsIcon size={16} color="#0891b2" />
                <span>Engineering Docs</span>
              </a>

              <button
                className={`devex-theme-switch${isDarkTheme ? ' dark' : ''}`}
                onClick={toggleTheme}
                aria-label="Toggle theme"
                role="switch"
                aria-checked={isDarkTheme}
              >
                <svg className="devex-theme-switch-icon sun" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58a.996.996 0 00-1.41 0 .996.996 0 000 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37a.996.996 0 00-1.41 0 .996.996 0 000 1.41l1.06 1.06c.39.39 1.03.39 1.41 0a.996.996 0 000-1.41l-1.06-1.06zm1.06-10.96a.996.996 0 000-1.41.996.996 0 00-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36a.996.996 0 000-1.41.996.996 0 00-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z"/>
                </svg>
                <div className="devex-theme-switch-knob" />
                <svg className="devex-theme-switch-icon moon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 3a9 9 0 109 9c0-.46-.04-.92-.1-1.36a5.389 5.389 0 01-4.4 2.26 5.403 5.403 0 01-3.14-9.8c-.44-.06-.9-.1-1.36-.1z"/>
                </svg>
              </button>
            </div>
          </header>

          {/* Page Content */}
          <div className="devex-page-content" id="primary-app-container" tabIndex={-1}>
            {children}
          </div>
        </main>
        </div>
      </div>

      {/* Profile Modal */}
      {showUserInfo && (
        <div className="devex-profile-modal-overlay" onClick={() => setShowUserInfo(false)}>
          <div className="devex-profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="devex-profile-modal-header">
              <h2>Profile</h2>
              <button
                className="devex-profile-modal-close"
                onClick={() => setShowUserInfo(false)}
              >
                ×
              </button>
            </div>
            <div className="devex-profile-modal-body">
              <pre>{JSON.stringify(accounts[0]?.idTokenClaims, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export { AppLayout };
